import type { DeletionBlockerDto, ListKpisOutput, Pillar, Unit, UserRole } from 'chery-contracts';
import { PILLARS } from 'chery-kpi-engine';

/** One row of `kpis.list`: a KPI plus its hierarchy depth. */
export type KpiRow = ListKpisOutput['items'][number];

export interface PillarGroup {
  pillar: Pillar;
  rows: KpiRow[];
}

/** KPI Setup is editable by ADMIN and PM; everybody else sees it read-only (plan §7). */
export const canManageKpis = (role: UserRole | undefined): boolean => role === 'ADMIN' || role === 'PM';

/** Rows grouped by pillar in the fixed pillar order, keeping the server's hierarchy order inside each group. */
export const groupByPillar = (rows: readonly KpiRow[]): PillarGroup[] =>
  PILLARS.map((pillar) => ({ pillar, rows: rows.filter((row) => row.pillar === pillar) })).filter((group) => group.rows.length > 0);

/** Short unit text for the list: `#`, `%`, `JOD`, `Days`… */
export const unitShortLabel = (unit: Unit, currency: string | null): string => {
  switch (unit) {
    case 'NUMBER':
      return '#';
    case 'PERCENTAGE':
      return '%';
    case 'CURRENCY':
      return currency ?? '—';
    case 'DAYS':
      return 'Days';
    case 'HOURS':
      return 'Hrs';
    case 'RATIO':
      return 'Ratio';
    case 'DECIMAL':
      return 'Dec';
    case 'TEXT':
      return 'Text';
  }
};

/** Remove `fromId` and re-insert it at `toId`'s position; unchanged when either is missing or they are equal. */
export const moveInList = (ids: readonly string[], fromId: string, toId: string): string[] => {
  const from = ids.indexOf(fromId);
  const to = ids.indexOf(toId);

  if (from < 0 || to < 0 || from === to) {
    return [...ids];
  }

  const next = [...ids];
  const [moved] = next.splice(from, 1) as [string];

  next.splice(to, 0, moved);

  return next;
};

/**
 * Display order only means something among siblings (same pillar, same parent), so ↑/↓ and drag-and-drop move a
 * KPI past a *sibling*. `visible` is the list on screen; the answer is the full pillar order to send to
 * `kpis.reorder` (which validates against every KPI of the pillar, inactive ones included), or `null` when there
 * is no sibling to move past.
 */
export const siblingMove = (visible: readonly KpiRow[], fullPillarIds: readonly string[], id: string, direction: 'up' | 'down'): string[] | null => {
  const row = visible.find((each) => each.id === id);

  if (!row) {
    return null;
  }

  const siblings = visible.filter((each) => each.pillar === row.pillar && each.parentKpiId === row.parentKpiId);
  const index = siblings.findIndex((each) => each.id === id);
  const target = siblings[direction === 'up' ? index - 1 : index + 1];

  return target ? moveInList(fullPillarIds, id, target.id) : null;
};

/** A row may be dropped onto another row of the same pillar with the same parent. */
export const canDropOn = (rows: readonly KpiRow[], draggedId: string, targetId: string): boolean => {
  if (draggedId === targetId) {
    return false;
  }

  const dragged = rows.find((row) => row.id === draggedId);
  const target = rows.find((row) => row.id === targetId);

  return Boolean(dragged && target && dragged.pillar === target.pillar && dragged.parentKpiId === target.parentKpiId);
};

/** Every descendant of `id` (children, grandchildren…) in an already-loaded list. */
export const descendantIds = (rows: readonly Pick<KpiRow, 'id' | 'parentKpiId'>[], id: string): Set<string> => {
  const found = new Set<string>();
  const queue = [id];

  while (queue.length > 0) {
    const current = queue.shift() as string;

    for (const row of rows) {
      if (row.parentKpiId === current && !found.has(row.id)) {
        found.add(row.id);
        queue.push(row.id);
      }
    }
  }

  return found;
};

/** Parent choices for a KPI: same pillar, not itself, not one of its own descendants (the server re-checks). */
export const parentOptions = (rows: readonly KpiRow[], pillar: Pillar, kpiId: string | null): KpiRow[] => {
  const excluded = kpiId === null ? new Set<string>() : new Set([kpiId, ...descendantIds(rows, kpiId)]);

  return rows.filter((row) => row.pillar === pillar && !excluded.has(row.id));
};

/** The structured blockers of a 409 from `kpis.delete` (`error.data`), or none when the body is anything else. */
export const blockersFrom = (data: unknown): DeletionBlockerDto[] => {
  if (typeof data !== 'object' || data === null) {
    return [];
  }

  const { blockers } = data as { blockers?: unknown };

  if (!Array.isArray(blockers)) {
    return [];
  }

  return blockers.filter(
    (blocker): blocker is DeletionBlockerDto =>
      typeof blocker === 'object' && blocker !== null && typeof (blocker as DeletionBlockerDto).message === 'string',
  );
};

/** Indentation for a hierarchy depth, in rem; capped so a deep tree never pushes the name out of its column. */
export const indentRem = (depth: number): number => Math.min(depth, 4) * 1.25;
